<template>
  <div v-bind:class="['single-box', stockStatusClass]">
    <div class="simple-box__top">
      <router-link class="single-box__link" :to="product.permalink">
        <div v-if="!hideImage">
          <Gallery
            :thumbnail="product.thumbnail"
            :displayOtherImages="false"
            :images="product.images"
            :canOpen="false"
          />
        </div>

        <div v-if="!hideName" class="single-box__name">
          <Badges :is_on_sale="product.is_on_sale"
                  :is_new="product.is_new"
                  :is_out_of_stock="product.stock_status == 'outofstock'"
                  :discount_percentage="product.discount_percentage || 0" />
          <h6 class="product-name">{{ productName | emptyDash }}</h6>
        </div>
      </router-link>

      <div v-if="isDangerousGoods" class="product-dangerous-goods-info">
        {{ translations.dangerousGoodsInfo }}
      </div>

      <div class="single-box__attributes">

        <div v-for="key in filteredAttributes" :key="key" :class="'shop-table__line shop-table__line__' + key">
          <span>{{ getAttributeLabel(key) }}:</span>
          <span class="shop-table__line__value">{{ getAttributeValue(key) }}</span>
        </div>

        <div class="shop-table__line shop-table__sku" v-if="!hideSKU">
          <span>{{ translations.productName }}:</span>
          <span class="shop-table__line__value">{{ product.sku | emptyDash }}</span>
        </div>

        <div v-if="!isRentCategory" class="shop-table__line">
          <span>{{ translations.status }}:</span>
          <span :class="`shop-table__line__` + product.stock_status">{{ getStockLabel }}</span>
        </div>
      </div>
    </div>

    <div class="single-box__actions">
      <div class="quantity__packages">
        <select v-if="packages.length && isPurchasable" name="package_type" ref="package_select">
          <option
            v-for="productPackage in packages"
            :key="productPackage.package_type_id"
            :value="productPackage.package_type_id"
            :selected="productPackage.package_type_id === selectedPackage"
          >
            {{ productPackage.package_type_name }}
            <template v-if="productPackage.package_type_id != 'PT'">
              ({{ inputToString(productPackage.qnty_in_package) }}{{ measurementUnit }})
            </template>
          </option>
        </select>
      </div>

      <div class="single-box__quantity">
        <div v-if="isPurchasable" class="quantity__wrapper">
          <button v-on:click="changeInputQuantity('decrease', $event)" class="js-quantity-down">
            <i data-feather="minus"></i>
          </button>
          <div class="quantity__amount">
            <input
              :min="minQuantity"
              :max="maxQuantity"
              :step="getPackageStep()"
              name="quantity"
              type="text"
              :value="inputValue"
              ref="add_to_cart_input"
              @invalid="customValidation"
              @change="checkBuyingStep"
            />
          </div>
          <span v-if="showMeasurementUnit">{{ measurementUnit }}</span>
          <button  v-on:click="changeInputQuantity('increase', $event)" class="js-quantity-up">
            <i data-feather="plus"></i>
          </button>
        </div>
      </div>

      <div class="single-box__price__wrapper">
        <span class="single-box__price__label" v-bind:class="{ 'store-table__price--discount': product.sale_price }">
          {{ translations.productPrice }}
          {{ getVatStatusTranslation() }}
        </span>

        <div class="single-box__price">
          <Price
          :regularPrice="getCurrentPackagePrice(Number(product.regular_price))"
          :salePrice="getCurrentPackagePrice(Number(product.sale_price))"
          />
          <span class="single-box__price__sale" v-if="product.sale_price"><i data-feather="percent"></i></span>
        </div>
      </div>

      <div class="single-box__add-to-cart">
        <form v-if="isPurchasable" :action="product.permalink" method="post" enctype="multipart/form-data" v-on:submit.prevent="addToCart"
        class="add-to-cart-form">
          <button
            type="submit"
            name="submit"
            :style="getAddToCartColor() ? { 'background-color': getAddToCartColor() } : {}"
            class="button"
          >
            <i data-feather="shopping-cart"></i>
            {{ translations.addToCart }}
          </button>

          <input type="hidden" name="action" value="add_to_cart" />
          <input type="hidden" name="product_id" :value="product.id" />
          <input type="hidden" name="quantity" :value="currentQuantity" />
          <input v-if="selectedPackage" type="hidden" name="package_type" :value="selectedPackage" />
        </form>

        <template v-else>
          <button
            @click="showForm('rent')"
            type="button"
            v-if="customForm === 'rent'"
            class="out-of-stock"
          >
            <i data-feather="message-square"></i>
            {{ translations.rentOutOfStock }}
          </button>

          <button
            @click="showForm('offer')"
            type="button"
            v-else-if="customForm === 'offer'"
            class="out-of-stock"
          >
            <i data-feather="message-square"></i>
            {{ translations.askForOffer }}
          </button>

          <button
            @click="showForm('')"
            type="button"
            v-else
            class="out-of-stock"
          >
            <i data-feather="message-square"></i>
            {{ translations.askForAvailability }}
          </button>
        </template>

        <div class="single-box__wishlist" v-if="displayWishlist">
          <Wishlist
            :productId="product.id"
            :regularPrice="Number(product.regular_price)"
            :salePrice="Number(product.sale_price)"
            :quantity="Number(currentQuantity)"
            :selectedPackage="selectedPackage"
          />
        </div>
      </div>
    </div>


    <div v-if="additionalInfo.length > 0" class="single_box__additional_info">
      <p>{{additionalInfo}}</p>
    </div>

    <div class="single-box__view-product" v-if="getProductButtonType === 'view_product'">

      <router-link class="single-box__view-product__link" :to="product.permalink">
        <button>{{translations.view_product_label}}</button>
      </router-link>
    </div>
  </div>
</template>

<script>
import Badges from "@/layouts/general-ui/Badges.vue";
import Price from "@/layouts/general-ui/Price.vue";
import Gallery from "@/layouts/general-ui/Gallery.vue";
import Wishlist from "@/layouts/general-ui/Wishlist.vue";
import FragmentsRenderer from '@/externals/FragmentRenderer';
import { Mutex } from 'async-mutex';
import Decimal from 'decimal.js';
import Choices from 'choices.js';
import feather from 'feather-icons';

let lock = new Mutex();

export default {
  name: "SimpleBox",
  components: {
    Badges,
    Price,
    Gallery,
    Wishlist
  },
  data() {
    this.isWishlistView = this.$router.currentRoute.name === "Wishlist";
    this.viewWishlistData = this.$store.getters.getViewWishlistItem(this.product.id);

    let selectedPackage;
    if (this.isWishlistView && this.viewWishlistData && this.viewWishlistData.package_type) {
      selectedPackage = this.viewWishlistData.package_type;
    } else {
      selectedPackage = this.getSelectedPackage();
    }

    let isPackageless = ( ! selectedPackage || selectedPackage === 'PT');
    let packages = this.parsePackageField();
    if (packages.length && ! selectedPackage) {
      selectedPackage = 'PT'; // Default to packageless option
    }
    let selectedPackageData = selectedPackage ? packages.find(e => e.package_type_id == selectedPackage) : undefined;
    let minQuantity = this.getPackageMinQty();

    let currentQuantity;
    if (this.isWishlistView && this.viewWishlistData && this.viewWishlistData.quantity > 0) {
      currentQuantity = Number(this.viewWishlistData.quantity);
    } else {
      currentQuantity = minQuantity;
    }

    return {
      changeAddToCartColor: false,
      errorMessage: '',
      lastValidation: 0,
      packages: packages,
      selectedPackage: selectedPackage,
      isPackageless: isPackageless,
      selectedPackageData: selectedPackageData,
      currentQuantity: currentQuantity,
      minQuantity: minQuantity,
      inputValue: this.inputToString(currentQuantity),
      fixedLength: ws_vue_store.fixed_length,
      showMeasurementUnit: ws_vue_store.show_measurement_unit === '1',
      translations: {
        askForOffer: ws_vue_store.translations.offer_product,
        askForAvailability: ws_vue_store.translations.ask_for_availability,
        offer:ws_vue_store.translations.offer_status,
        rentOutOfStock: ws_vue_store.translations.rent_out_of_stock,
        addToCart: ws_vue_store.translations.add_to_cart,
        product_added_to_cart: ws_vue_store.translations.product_added_to_cart,
        status: ws_vue_store.translations.status,
        instock: ws_vue_store.translations.instock,
        outofstock: ws_vue_store.translations.out_of_stock,
        backorder: ws_vue_store.translations.backorder,
        view_product_label: ws_vue_store.translations.view_product_label,
        productName: ws_vue_store.translations.product_name,
        productPrice: ws_vue_store.translations.product_price,
        withVat: ws_vue_store.translations.included_vat,
        withoutVat: ws_vue_store.translations.excluded_vat,
        dangerousGoodsInfo: ws_vue_store.translations.dangerous_goods_info,
      }
    };
  },
  props: {
    product: Object,
    hideImage: {
      type: Boolean,
      default() {
        return false;
      }
    },
    hideName: {
      type: Boolean,
      default() {
        return false;
      }
    },
    hideSKU: {
      type: Boolean,
      default() {
        return false;
      }
    },
    additionalInfo: {
      type: String,
      default() {
        return '';
      }
    },
    displayWishlist: {
      type: Boolean,
      default() {
        return ws_vue_store.display_wishlist === "1";
      },
    }
  },
  filters: {
    emptyDash(value) {
      if (!value) return "-";
      return value;
    }
  },
  mounted() {
    if (this.$refs.package_select) {
      new Choices(this.$refs.package_select, {
        searchEnabled: false,
        searchChoices: false,
        shouldSort: false
      });
      this.$refs.package_select.addEventListener('change', e => this.packageSelected(e));
    }

    feather.replace();
  },
  computed: {
    productName() {
      // Convert special characters from HTML entity codes to characters
      let input = document.createElement("textarea");
      input.innerHTML = this.product.name;
      return input.value;
    },
    includingTax() {
      return this.$store.getters.getTaxesEnabled;
    },
    isPurchasable() {
      return this.product.stock_status === 'instock'  && !this.customForm;
    },
    customForm() {
      return (this.product.custom_form) ? this.product.custom_form : '';
    },
    maxQuantity() {
      return Number(this.product.get_max_purchase_quantity);
    },
    measurementUnit() {
      return this.product.measurement_unit != undefined && this.showMeasurementUnit ? this.product.measurement_unit : '';
    },
    isRentCategory() {
      return this.$store.getters.getIsRentCategory;
    },
    isDangerousGoods() {
      return this.product.dangerous_goods === 'yes';
    },
    filteredAttributes() {
      let available = this.product.attributes.map(t => t.key);
      let displayedAttributeTaxonomies = this.$store.getters.getAllowedAttributes;
      return displayedAttributeTaxonomies.filter(t => available.includes(t));
    },
    getStockLabel() {
      switch (this.product.stock_status) {
        case 'instock':
          return this.translations.instock;
        case 'outofstock':
          return this.translations.outofstock;
        case 'onbackorder':
          return this.translations.backorder;
        case 'offer':
          return this.translations.offer;
      }
      return this.product.stock_status;
    },
    getProductButtonType() {
      if ( ws_vue_store.config.product_button === 'add_to_cart' ) {
        return 'add_to_cart';
      }
      if ( ws_vue_store.config.product_button === 'view_product' ) {
        return 'view_product';
      }

      return '';
    },
    stockStatusClass() {
      if ( this.product.stock_status === 'instock' ) {
        return '';
      }

      return 'out-of-stock';
    },
    singleAttribute(){
      if(ws_vue_store.config.kaali_allowed_attributes === undefined || ws_vue_store.config.kaali_allowed_attributes.length !== 1 ) return false;

      let attribute = this.product.attributes.find(attr => ws_vue_store.config.kaali_allowed_attributes.includes( attr.key ));
      if ( attribute ) {
          return attribute.options.map( option => option.name ).join( ' ' );
      }

      return false;
    },
    multipleAttributes() {
      if(ws_vue_store.config.kaali_allowed_attributes === undefined || ws_vue_store.config.kaali_allowed_attributes.length <= 1) return false;

      let attributes = this.product.attributes.filter(attr => ws_vue_store.config.kaali_allowed_attributes.includes( attr.key ));
      if ( attributes ) {
        let processed = [];
        const separator = ws_vue_store.separate_attributes === '1' ? ', ' : ' ';

        for (const attr in attributes) {
          processed.push({
            'label': attributes[attr].label,
            'content': attributes[attr].options.map(
              t => `<span class="single-box__multiple-attributes__attribute-value__option">${t.name}</span>`
            ).join(separator),
          });
        }
        return processed;
      }

      return false;
    }
  },
  methods: {
    getVatStatusTranslation() {
      if (this.includingTax) {
        return this.translations.withVat;
      } else {
        return this.translations.withoutVat;
      }
    },
    setInputValue(quantity, step, maxQuantity) {
      let calculation;

      if (+(quantity % step).toFixed(2) !== 0 || quantity > maxQuantity) {
        if (Math.ceil(quantity / step) * step > maxQuantity) {
          let qty = Math.ceil(maxQuantity / step);
          calculation = qty * step <= maxQuantity ? qty * step : qty * step - step;
        } else {
          calculation = Math.ceil(quantity / step) * step;
        }

        // Round the value
        if (Number.isSafeInteger(step)) {
          calculation = Math.round(calculation);
        } else {
          calculation = +(calculation).toFixed(2);
        }
      } else {
        calculation = quantity;
      }

      this.inputValue = this.inputToString(Math.abs(calculation));
      this.$refs.add_to_cart_input.value = this.inputValue;
    },
    showNotEnoughForm() {
      const modalForm = document.getElementById('ws-not-enough-form');
      const pageShadow = document.getElementById('js-page-shadow');
      const pageBody = document.querySelector('body');

      if (!modalForm || modalForm.classList.contains('active')) return;

      let maxQty = this.maxQuantity;
      let subjectContent = this.productName + ' - ' + this.product.sku;
      let unit = this.measurementUnit;

      modalForm.querySelector('.product-name').textContent = subjectContent;
      modalForm.querySelector('input[name="your-subject"]').value = subjectContent;
      modalForm.querySelector('.js_not_enough_qty').innerText = maxQty;
      modalForm.querySelector('.js_not_enough_unit').innerText = unit;

      modalForm.classList.add('active');
      pageShadow.style.display = 'block';
      pageBody.style.overflowY = 'hidden';
    },
    isOpenNotEnoughForm() {
      const modalForm = document.getElementById('ws-not-enough-form');
      return modalForm && modalForm.classList.contains('active');
    },
    // Convert a string number with a custom separator to a valid number
    inputToNumber(value) {
      return Number(value.replace(this.$store.state.numberSeparator, '.'));
    },
    customValidation() {
      this.checkBuyingStep();
      let el = this.$refs.add_to_cart_input;
      let max = el.getAttribute('max');
      let value = this.inputToNumber(this.inputValue);

      if (parseInt(value) > parseInt(max)) {
        el.setCustomValidity(ws_vue_store.translations.add_to_cart_invalid_message);
      } else {
        el.setCustomValidity('');
      }
    },
    checkBuyingStep() {
      this.lastValidation = Date.now();
      let stopAddToCart = false;
      let quantity = this.inputToNumber(this.$refs.add_to_cart_input.value);
      if (isNaN(quantity)) quantity = this.minQuantity;

      if (this.isOpenNotEnoughForm()) {
        stopAddToCart = true;
      } else if (quantity > this.maxQuantity) {
        this.showNotEnoughForm();
        stopAddToCart = true;
      }

      this.setInputValue(quantity, this.minQuantity, this.maxQuantity);
      this.triggerQuantityChange(quantity);
      return stopAddToCart;
    },
    getPackageStep() {
      if (this.isPackageless || this.selectedPackageData === undefined) {
        return this.product.buying_step != undefined ? this.product.buying_step : 1;
      } else {
        return this.selectedPackageData.qnty_in_package;
      }
    },
    changeInputQuantity(trend, event) {
      event.preventDefault();

      // Pressing "enter" key on quantity input also triggers "-" button
      // This checks that "enter" key wasn't used right now for changing quantity
      if ((Date.now() - this.lastValidation) < 500) {
        return;
      }

      let el = this.$refs.add_to_cart_input;
      let max = new Decimal(el.getAttribute('max'));
      let min = new Decimal(el.getAttribute('min'));
      let step = new Decimal(el.getAttribute('step'));

      let current_quantity = this.inputToNumber(el.value);
      current_quantity = new Decimal(current_quantity);

      let new_value = new Decimal(0);
      if (trend === 'increase' && (current_quantity.plus(step)).lte(max)) {
        new_value = current_quantity.plus(step);
      }
      if (trend === 'decrease' && (current_quantity.minus(step)).gte(min)) {
        new_value = current_quantity.minus(step);
      }

      if (!new_value.equals(new Decimal(0))) {
        this.inputValue = this.inputToString(new_value);
        this.triggerQuantityChange(new_value.toString());
      }
    },
    getPackageMinQty() {
      if (this.isPackageless || this.selectedPackageData === undefined) {
        return Number(this.product.get_min_purchase_quantity);
      } else {
        return this.selectedPackageData.qnty_in_package;
      }
    },
    parsePackageField() {
      if (this.product.packages) {
        try {
          let data = JSON.parse(this.product.packages);
          if (data.find) {
            return data; // Check if parsed data is array
          }
        } catch {}
      }
      return [];
    },
    getSelectedPackage() {
      let selectedPackages = this.$store.getters.getSelectedPackages;
      return selectedPackages[this.product.id] ? selectedPackages[this.product.id] : '';
    },
    getCurrentPackageData() {
      if (this.selectedPackage) {
        return this.packages.find(e => e.package_type_id == this.selectedPackage);
      }
      return undefined;
    },
    getCurrentPackagePrice(price) {
      return Number((price * this.currentQuantity).toFixed(this.fixedLength));
    },
    packageSelected(event) {
      this.$store.commit('addSelectedPackages', {
        id: this.product.id,
        package: event.currentTarget.value
      });
      this.triggerPackageChanged();

      let selectedPackageData = this.packages.find(e => e.package_type_id == event.currentTarget.value);
      if (selectedPackageData) {
        this.inputValue = this.inputToString(selectedPackageData.qnty_in_package);
        this.triggerQuantityChange(selectedPackageData.qnty_in_package.toString());
      }
    },
    triggerPackageChanged() {
      this.selectedPackage = this.getSelectedPackage();
      this.isPackageless = ( ! this.selectedPackage || this.selectedPackage === 'PT');
      this.currentQuantity = this.isPackageless ? this.minQuantity : 1;
      this.selectedPackageData = this.getCurrentPackageData(),
      this.$forceUpdate();
    },
    triggerQuantityChange(qty) {
      if (this.isWishlistView && this.$store.getters.getIsViewWishlistOwner) {
        this.$store.dispatch('updateWishlist', {
          old_quantity: this.currentQuantity,
          price: this.product.sale_price ? this.product.sale_price : this.product.regular_price,
          product_id: this.product.id,
          package: this.selectedPackage,
          quantity: qty
        });
      }

      this.currentQuantity = qty;
      this.$forceUpdate();
    },
    // Convert a valid number to string with a different comma separator
    inputToString(value) {
      return value.toString().replace('.', this.$store.state.numberSeparator);
    },
    getAttributeLabel(taxonomySlug) {
      const taxonomy = this.$store.getters.getAttributeTaxonomy(taxonomySlug);
      if (taxonomy) {
        return taxonomy.label;
      }
      return '';
    },
    getAttributeValue(taxonomySlug) {
      const foundAttr = this.product.attributes.find(attribute => attribute.key == taxonomySlug);
      if(foundAttr == undefined) return '';

      let value = foundAttr.options.map(option => option.name).join(', ');
      // Decode html entity codes
      let textArea = document.createElement('textarea');
      textArea.innerHTML = value;

      return textArea.value;
    },
    // AddToCart.vue functions
    async addToCart(event) {
      if (this.checkBuyingStep()) {
        return; // Stop processing when not enough window is open
      };

      this.isLoading = true;
      let release = await lock.acquire();

      let formData = new FormData(event.target);
      if (formData.get('quantity')) {
        formData.set('quantity', this.inputToNumber(formData.get('quantity')));
      }

      this.axios
        .post(
          wc_add_to_cart_params.wc_ajax_url
            .toString()
            .replace('%%endpoint%%', 'add_to_cart'),
          formData
        )
        .then((result) => {
          if (result.data.fragments) {
            this.isLoading = false;
            FragmentsRenderer.renderFragments(result.data.fragments);
            this.displayWoocommerceNotice(this.translations.product_added_to_cart, false);
            this.$store.commit('setRecentlyAddedProduct', this.product.id);
          } else if (result.data.error) {
            this.getErrorMessages();
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          release();
        });
    },
    getErrorMessages() {
      this.axios
        .get(ws_vue_store.ajax_url + '?action=get_cart_error_messages')
        .then((response) => {
          this.displayWoocommerceNotice(response.data, 'woocommerce-error');
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
    },
    displayWoocommerceNotice(message, extraClass) {
      document.querySelectorAll('.woocommerce-notices-wrapper').forEach(wrapper => {
        wrapper.remove();
      });

      let wrapper = document.createElement('div');
      let notice = document.createElement('div');

      wrapper.classList.add('woocommerce-notices-wrapper');
      notice.classList.add('woocommerce-message');
      if (extraClass) notice.classList.add(extraClass);
      notice.role = 'alert';
      notice.tabIndex = -1;
      notice.innerHTML = message;

      wrapper.append(notice);
      document.querySelector('#content').append(wrapper);
    },
    showForm(type) {
      let formID = '';
      switch (type) {
        case 'rent':
          formID = '#rent-out-of-stock-form'; break;
        case 'offer':
          formID = '#offer-product-form'; break;
        default:
          formID = '#out-of-stock-form';
      }

      const modalForm = document.querySelector(formID);
      const pageShadow = document.getElementById('js-page-shadow');
      const pageBody = document.querySelector('body');

      if (!modalForm)
        return;

      let subjectContent = this.productName + ' - ' + this.product.sku;

      modalForm
        .querySelector('input[name="your-subject"]')
        .value = subjectContent;
      modalForm
        .querySelector('.js-out-of-stock-subject')
        .innerText = subjectContent;

      modalForm.classList.add('active');
      pageShadow.style.display = 'block';
      pageBody.style.overflowY = 'hidden';
    },
    getAddToCartColor() {
      if (
        ws_vue_store.change_add_to_cart_color == '1' &&
        (this.changeAddToCartColor ||
          ws_vue_store.cart_items.includes(this.product.id) ||
          this.$store.getters.getRecentlyAddedProducts.includes(this.product.id))
      ) {
        return ws_vue_store.add_to_cart_color;
      }
      return '';
    }
  }
};
</script>
